import type { CSSProperties, ReactNode } from 'react';
import { Black, FONT_FAMILY, Gray50, Gray700, Gray900, RADIUS_SM, SHADOW_CLOSE, SP, Violet50, Violet600, White } from './tokens';
import type { IndexedPopupButton } from './types';
import { toSafeOpenUrl } from './utils';

// 우하단 카드는 200자 본문도 허용하되, CTA가 뷰포트 밖으로 밀리지 않도록 본문만 스크롤한다.
const BOTTOM_RIGHT_BODY_MAX_HEIGHT = '112px';
export function TextBlock({
  title,
  body
}: {
  title: string;
  body: string;
}) {
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: SP['3xs']
  }} data-sentry-component="TextBlock" data-sentry-source-file="parts.tsx">
      {title && <TitleRow title={title} />}
      {body && <BodyText>{body}</BodyText>}
    </div>;
}
export function TitleRow({
  title,
  rightSlot
}: {
  title: string;
  rightSlot?: ReactNode;
}) {
  return <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: SP['3xs']
  }} data-sentry-component="TitleRow" data-sentry-source-file="parts.tsx">
      <span style={{
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: 1.4,
      color: Gray900,
      flex: 1
    }}>
        {title}
      </span>
      {rightSlot}
    </div>;
}
export function BodyText({
  children
}: {
  children: ReactNode;
}) {
  return <div tabIndex={0} style={{
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.4,
    color: Gray700,
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
    maxHeight: BOTTOM_RIGHT_BODY_MAX_HEIGHT,
    overflowY: 'auto',
    overscrollBehavior: 'contain'
  }} data-sentry-component="BodyText" data-sentry-source-file="parts.tsx">
      {children}
    </div>;
}
export function Buttons({
  buttons,
  onButtonClick,
  onClose
}: {
  buttons: IndexedPopupButton[];
  onButtonClick?: (index: number, url?: string) => void;
  onClose?: () => void;
}) {
  if (buttons.length === 0) return null;
  return <div style={{
    display: 'flex',
    gap: SP['2xs']
  }} data-sentry-component="Buttons" data-sentry-source-file="parts.tsx">
      {buttons.map(btn => {
      const isPrimary = btn.originalIndex === buttons.length - 1;
      return <button key={`btn-${btn.originalIndex}`} type="button" onClick={e => {
        e.preventDefault();
        onButtonClick?.(btn.originalIndex, btn.url);
        const safeUrl = btn.url ? toSafeOpenUrl(btn.url) : null;
        if (safeUrl) {
          window.open(safeUrl, '_blank', 'noopener,noreferrer');
        }
        onClose?.();
      }} style={{
        flex: 1,
        height: '40px',
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: FONT_FAMILY,
        border: 'none',
        borderRadius: RADIUS_SM,
        cursor: 'pointer',
        backgroundColor: isPrimary ? Violet50 : Gray50,
        color: isPrimary ? Violet600 : Gray700,
        transition: 'background-color 0.15s'
      }}>
            {btn.label}
          </button>;
    })}
    </div>;
}
export function DismissTextButton({
  dismissText,
  onDismiss,
  onClose
}: {
  dismissText?: string;
  onDismiss?: () => void;
  onClose?: () => void;
}) {
  if (!dismissText) return null;
  return <button type="button" onClick={() => {
    onDismiss?.();
    onClose?.();
  }} style={{
    width: '100%',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.4,
    color: Gray700,
    opacity: 0.7,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0
  }} data-sentry-component="DismissTextButton" data-sentry-source-file="parts.tsx">
      {dismissText}
    </button>;
}
export function CloseButton({
  onClick,
  style,
  withShadow
}: {
  onClick: () => void;
  style?: CSSProperties;
  withShadow?: boolean;
}) {
  return <button onClick={onClick} type="button" aria-label="닫기" style={{
    width: '26px',
    height: '26px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: White,
    border: 'none',
    borderRadius: '9999px',
    cursor: 'pointer',
    color: Gray700,
    boxShadow: withShadow ? SHADOW_CLOSE : 'none',
    flex: 'none',
    padding: 0,
    ...style
  }} data-sentry-component="CloseButton" data-sentry-source-file="parts.tsx">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" data-sentry-element="svg" data-sentry-source-file="parts.tsx">
        <path d="M6 6L18 18M6 18L18 6" stroke={Black} strokeWidth="2" strokeLinecap="round" data-sentry-element="path" data-sentry-source-file="parts.tsx" />
      </svg>
    </button>;
}